import { DocumentTemplateType, QueryFactory } from '@lmt/shared-types';
import { useEventCallback } from '@mui/material';
import { TemplateDto } from '@lmt/shared-types';

import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  defaultDocumentName,
  isDocumentNew,
  saveAsTemplateMenuId,
} from './richedit-helper';
import { assertNever } from '@lmt/unicorn-shared/app/helpers/assert-never';
import {
  DXR,
  RichEdit,
  SavingEventArgs,
} from '../../../common/devexpress/RichEdit/dx-richedit';
import i18next from 'i18next';
import { useTabId } from '../../keep-alive/TabContext';
import { getCurrentlyOpenedTabId } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { NavigationActions } from '@lmt/unicorn-shared/app/features/navigation/reducer';

export function getTemplateClient(templateType: DocumentTemplateType) {
  switch (templateType) {
    case DocumentTemplateType.HL7OutgoingMessage:
      return QueryFactory.HL7OutgoingTemplatesClient;
    case DocumentTemplateType.GDTOutgoingMessage:
      return QueryFactory.GDTOutgoingTemplatesClient;
    case DocumentTemplateType.Document:
    case DocumentTemplateType.DocumentHeader:
    case DocumentTemplateType.DocumentFooter:
      return QueryFactory.DocumentTemplateClient;

    default:
      assertNever(templateType);
  }
}

type Props = {
  richEdit: React.MutableRefObject<RichEdit>;
  // if undefined, SaveAs dialog will be shown when saving new document
  saveNewDocumentErrorMessage?: string;
  currentTemplate: TemplateDto | null;
  setCurrentTemplate: (template: TemplateDto | null) => void;
  templateType: DocumentTemplateType;
};

/*
  Hook that handles "Save" action and adds "Save as" menu item
 */
export const SaveTemplateRichEditButton: React.FC<Props> = (props) => {
  const { richEdit, currentTemplate, setCurrentTemplate, templateType } = props;
  const dispatch = useUniDispatch();
  const modals = useModal();
  const queryClient = useQueryClient();
  const tabId = useTabId();

  const setTabHasUnsavedChanges = useEventCallback((hasChanges: boolean) => {
    if (tabId) {
      dispatch(
        NavigationActions.tabHasUnsavedChangesChanged({
          id: tabId,
          hasUnsavedChanges: hasChanges,
        }),
      );
    }
  });

  const checkIfDocumentHasUnsavedChanges = useEventCallback(
    (s: RichEdit | undefined) => {
      if (!s || isDocumentNew(s)) {
        setTabHasUnsavedChanges(true);
        return;
      }
      if (s?.hasUnsavedChanges) {
        setTabHasUnsavedChanges(true);
      } else {
        setTabHasUnsavedChanges(false);
      }
    },
  );

  const sendSaveTemplateRequest = useEventCallback(
    async (
      templateId: number,
      title: string,
      content: string,
    ): Promise<boolean> => {
      try {
        const client = getTemplateClient(templateType);
        if (templateId) {
          await client.patch(templateId, {
            title: title,
            documentContent: content,
          });
          await queryClient.invalidateQueries({
            queryKey:
              QueryFactory.DocumentTemplateQuery.getSingleQueryKey(templateId),
          });
          await queryClient.invalidateQueries({
            queryKey:
              QueryFactory.GDTOutgoingTemplatesQuery.getSingleQueryKey(
                templateId,
              ),
          });
          await queryClient.invalidateQueries({
            queryKey:
              QueryFactory.HL7OutgoingTemplatesQuery.getSingleQueryKey(
                templateId,
              ),
          });
        } else {
          // create new template
          if (props.saveNewDocumentErrorMessage) {
            await modals.showError({ text: props.saveNewDocumentErrorMessage });
            return false;
          }
          if (templateType === DocumentTemplateType.HL7OutgoingMessage) {
            const result = await QueryFactory.HL7OutgoingTemplatesClient.create(
              {
                title: title,
                content: content,
                type: templateType,
              },
            );
            setCurrentTemplate(result);
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.HL7OutgoingTemplatesQuery.getQueryKey(),
            });
            await queryClient.invalidateQueries({
              queryKey:
                QueryFactory.HL7OutgoingTemplatesQuery.getSingleQueryKey(
                  result.id,
                ),
            });
          } else if (templateType === DocumentTemplateType.GDTOutgoingMessage) {
            const result = await QueryFactory.GDTOutgoingTemplatesClient.create(
              {
                title: title,
                content: content,
                type: templateType,
              },
            );
            setCurrentTemplate(result);
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.GDTOutgoingTemplatesQuery.getQueryKey(),
            });
            await queryClient.invalidateQueries({
              queryKey:
                QueryFactory.GDTOutgoingTemplatesQuery.getSingleQueryKey(
                  result.id,
                ),
            });
          } else {
            const result = await QueryFactory.DocumentTemplateClient.create({
              title: title,
              content: content,
              type: templateType,
            });
            setCurrentTemplate(result);
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.DocumentTemplateQuery.getQueryKey(),
            });
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.DocumentTemplateQuery.getSingleQueryKey(
                result.id,
              ),
            });
          }
        }
      } catch (e) {
        // if the tab was already closed - do not show any error
        if (tabId !== getCurrentlyOpenedTabId()) return false;

        await modals.showError({ text: convertToErrorString(e) });
        return false;
      }
      return true;
    },
  );

  const saveTemplate = useEventCallback(
    async (s: RichEdit | undefined, e: SavingEventArgs | undefined) => {
      if (!s || !e) {
        return;
      }

      if (isDocumentNew(s) && e.fileName === defaultDocumentName) {
        richEdit.current.events.customCommandExecuted._fireEvent(
          s,
          new DXR.CustomCommandExecutedEventArgs(saveAsTemplateMenuId, null),
        );
      } else {
        try {
          e.handled = true; // makes "Save" button disabled
          s.loadingPanel.show();
          const titleChanged =
            e.fileName !== currentTemplate?.title &&
            (!currentTemplate?.isDefault ||
              e.fileName !== i18next.t(currentTemplate?.title as any));
          const saveAsNew = titleChanged || !currentTemplate?.id; // saveAs new document
          const isSaveSuccessful = await sendSaveTemplateRequest(
            saveAsNew ? 0 : currentTemplate!.id,
            saveAsNew ? e.fileName : currentTemplate!.title,
            e.base64,
          );
          if (isSaveSuccessful) {
            s.fileName = e.fileName;
            setTabHasUnsavedChanges(false);
          } else {
            s.hasUnsavedChanges = true;
          }
        } catch (e) {
          // if the tab was already closed - do not show any error
          if (tabId !== getCurrentlyOpenedTabId()) return;
          await modals.showError({ text: convertToErrorString(e) });
        } finally {
          try {
            s.loadingPanel.hide();
          } catch (e) {}
        }
      }
    },
  );

  // adds handler for Saving
  useEffect(() => {
    richEdit.current.events.saving.addHandler(saveTemplate);
    richEdit.current.events.documentChanged.addHandler(
      checkIfDocumentHasUnsavedChanges,
    );
    return () => {
      richEdit.current.events.saving.removeHandler(saveTemplate);
      richEdit.current.events.documentChanged.removeHandler(
        checkIfDocumentHasUnsavedChanges,
      );
    };
  }, []);

  return null;
};
